import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialDefinitionList, EditorialSubsection } from '../../report/editorial';

interface Props {
  report: SiteRagReport;
  label: string;
}

/**
 * W2d editorial port — the two uppercase-tracking `<h3>` prose blocks
 * (Site summary / Brief objective) become EditorialSubsection (hairline
 * rule + small-caps mono label); the counterparties / key-dates footer
 * strip becomes an EditorialDefinitionList ledger (label/value shape).
 * Authored strings are unchanged and citation walking is preserved;
 * empty / whitespace-only inputs drop their block (the ledger renders
 * only the rows that carry real values).
 *
 * Mounted via SectionsRouter (`projectContext`); export name + props
 * signature are load-bearing for the registry — do not change them.
 */
export const CustomBriefProjectContextSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const ctx = report.customBriefProjectContext;
  const hasData = Boolean(ctx?.siteSummary?.trim() || ctx?.intentRestatement?.trim());

  const ledgerItems = [
    ...(report.customBriefCounterparties?.trim()
      ? [{ label: 'Counterparties', value: report.customBriefCounterparties }]
      : []),
    ...(report.customBriefKeyDates?.trim()
      ? [{ label: 'Key dates', value: report.customBriefKeyDates }]
      : []),
  ];

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {!ctx ? null : (
        <article className="space-y-4">
          {ctx.siteSummary.trim() && (
            <EditorialSubsection theme={theme} label="Site summary">
              <p className="leading-relaxed">
                {walkCitationText(ctx.siteSummary, report.references)}
              </p>
            </EditorialSubsection>
          )}
          {ctx.intentRestatement.trim() && (
            <EditorialSubsection theme={theme} label="Brief objective">
              <p className="leading-relaxed">
                {walkCitationText(ctx.intentRestatement, report.references)}
              </p>
            </EditorialSubsection>
          )}
          {ledgerItems.length > 0 && (
            // Carrier div holds the testid (EditorialDefinitionList has no testid prop).
            <div data-testid="custom-brief-context-ledger">
              <EditorialDefinitionList theme={theme} items={ledgerItems} />
            </div>
          )}
        </article>
      )}
    </SectionShell>
  );
};
